import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { Emitter, type Event } from '@patsnap/tahiti-core';
import type {
    HistoryState,
    HistoryDetail,
    HistoryDetailTemp,
} from '../type/history.type';
import { HistoryModel } from '../model/history.model';
import {
    GetTaskIdDTO,
    PutHistoryDTO,
    GetHistoryDetailDTO,
} from './history.repository.interface';

export const HistoryService = Symbol('HistoryService');

export interface HistoryService {
    getModelData: () => HistoryState;
    readonly onStatesChange: Event<HistoryState>;
    readonly onTaskTitleChange: Event<string>;
    createTaskId: (type: string) => Promise<string>;
    updateTitle: (title: string) => Promise<void>;
    putHistory: (params: { [key: string]: any }) => Promise<void>;
    getHistoryDetail: (taskId: string) => Promise<HistoryDetail>;
    updateTempData: (getUpdateTempData: (temp: HistoryDetailTemp) => HistoryDetailTemp) => void;
}

@injectable()
export class HistoryServiceImpl implements HistoryService {
    private _onStatesChangeEmitter = new Emitter<HistoryState>();
    private _onTaskTitleChangeEmitter = new Emitter<string>();
    readonly onStatesChange = this._onStatesChangeEmitter.event;
    readonly onTaskTitleChange = this._onTaskTitleChangeEmitter.event;

    constructor(
        @inject(HistoryModel)
        private _model: HistoryModel,
        @inject(GetTaskIdDTO)
        private _getTaskIdDTO: GetTaskIdDTO,
        @inject(PutHistoryDTO)
        private _putHistoryDTO: PutHistoryDTO<{ [key: string]: any }>,
        @inject(GetHistoryDetailDTO)
        private _getHistoryDetailDTO: GetHistoryDetailDTO
    ) {
        this._model.onStatesChange(this._emitModelDataChange.bind(this));
    }

    private _emitModelDataChange() {
        this._onStatesChangeEmitter.fire(this.getModelData());
    }

    public getModelData(): HistoryState {
        return this._model.modelData;
    }

    private _setModelData(value: Partial<HistoryState>) {
        this._model.update(modelData => {
            return { ...modelData, ...value };
        });
    }

    public updateTempData(getUpdateTempData: (temp: HistoryDetailTemp) => HistoryDetailTemp) {
        const temp = this.getModelData().historyDetailTemp;

        this._setModelData({ historyDetailTemp: getUpdateTempData(temp) });
    }

    public async updateTitle(title: string) {
        const temp = this.getModelData().historyDetailTemp;
        this._setModelData({ historyDetailTemp: { ...temp, title } });
        await this.putHistory({ title });
        this._onTaskTitleChangeEmitter.fire(title);
    }

    public async putHistory(params: { [key: string]: any }) {
        const temp = this.getModelData().historyDetailTemp;

        await this._putHistoryDTO.fetch({ task_id: temp.taskId, ...params });
    }

    public async getHistoryDetail(taskId: string) {
        const res = await this._getHistoryDetailDTO.fetch(taskId);
        this._setModelData({ historyDetailTemp: { title: res.title || '', taskId } });
        return res;
    }

    public async createTaskId(type: string) {
        const taskId = await this._getTaskIdDTO.fetch(type);
        const temp = this.getModelData().historyDetailTemp;

        this._setModelData({ taskId, historyDetailTemp: { ...temp, taskId } });
        return taskId;
    }
}
